import { filter, find, first, get, isNil, map, some, toNumber } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { IconButton } from "@/shared/ui/IconButton";
import { SummarySectionsGroup } from "@/shared/widgets/SummarySectionsGroup";
import { Title } from "@/shared/ui/Title";
import { CompetencyMapSection } from "@/shared/widgets/CompetencyMapSection";
import { PersonalityMapSection } from "@/shared/widgets/PersonalityMapSection";
import { ProfileValuesSection } from "@/features/profile/components/ProfileValuesSection";
import { useDimensionsWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { useTeamDetails } from "@/features/teams/hooks/useTeamDetails";
import ProfileHeader from "@/features/profile/components/ProfileHeader";
import { usePeople } from "@/features/people/hooks/usePeople";
import { NotesSection } from "@/shared/widgets/NotesSection";
import { useProject } from "@/features/projects/hooks/usePosition";
import { WisnioProfileHighlightsSection } from "@/features/profile/components/WisnioProfileHighlightsSection";
import { Breadcrumbs } from "@/shared/widgets/Breadcrumbs";
import { useScorecard } from "@/features/scorecards/hooks/useScorecard";
import { HeaderContent } from "@/shared/widgets/HeaderContent";
import { PDFExportTrigger } from "@/shared/pdf";
import CandidateProfilePdf from "@/shared/pdf/Templates/Profiles/CandidateProfile";
import { DataSection } from "@/shared/widgets/DataSection";
import ProfileLayout from "@/features/profile/components/ProfileLayout";
import { useAssessmentsList } from "@/features/assessments/hooks/useAssessmentsList";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { usePersonProfileData } from "@/features/people/hooks/usePersonProfileData";
import { ScorecardRow } from "@/features/scorecards/graphql/generated";
import ProfileSectionWrapper from "@/features/profile/components/ProfileLayout/ProfileSectionWrapper";
import { useProjectNotes } from "@/features/projects/hooks/useProjectNotes";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import { EVENT_TYPES, useEvent } from "@/shared/contexts/event.context";
import { CandidateCompetencyInsightsSection } from "@/shared/widgets/InsightsSection/CandidateInsights";
import { getGptFlowKey } from "@/shared/constants/gpt-flow-types";
import {
  useProfileCompletion,
  useProfileLayoutProps,
} from "@/features/profile/hooks";
import ProfileEmptyState from "@/features/profile/components/ProfileEmptyState";
import usePerson from "@/features/people/hooks/usePerson";
import ShareCandidateTrigger from "@/features/share/components/ShareCandidateTrigger";
import FileUploadSection from "@/shared/widgets/FileUploadSection";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { AccessLevel, RoleType } from "@/shared/constants";
import { PPTGeneratorButton } from "@/shared/widgets/PPTGeneratorButton";
import { useViewer } from "@/features/workspace/hooks/useViewer";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import LeadershipSpanWidget, {
  LEADERSHIP_SPAN_DIFFERENTIATORS,
} from "@/shared/widgets/LeadershipSpan";
import { LSpanKey } from "@/lib/leadership-span-bucket-score-parser";
import { FeatureGate } from "@/shared/widgets/FeatureFlags";
import { FeatureFlagKey } from "@/types/feature-flags";
import { ResourceEnum } from "@/shared/constants/resources";
import { getPersonRoleObject } from "@/shared/utils/selectors/getRolesAndAccess";
import { PersonRole } from "@/features/people/graphql/generated";
import { cleanupMarkdownFromTags } from "@/shared/utils/parsers/cleanupMarkdownFromTags";
import { useAttachments } from "@/shared/hooks";

function CandidateProfile() {
  const {
    teamId: _teamId,
    candidateId: _candidateId,
    personId: _personId,
    projectId: _projectId,
  } = useParams();

  const { viewer } = useViewer();

  const navigate = useOrgNavigate();

  const candidateId = toNumber(_candidateId) || toNumber(_personId);

  const projectId = toNumber(_projectId);

  const { loading: loadingProject, ...project } = useProject(projectId);

  const { getAccountConfigValue } = useAccount();

  const { validateAccessToResource } = useViewerPermissions();

  const hasPermissionsToEdit = validateAccessToResource({
    resourceType: ResourceEnum.project,
    accessLevel: AccessLevel.full,
    resourceId: projectId,
  });

  const { people: projectPeople } = usePeople({
    project_id: projectId,
  });

  // The people query returns everyone linked to the project, collaborators
  // included — only project-role CANDIDATE belongs in the candidate switcher
  const otherCandidates = useMemo(
    () =>
      filter(projectPeople, (person) => {
        const personRole = getPersonRoleObject(
          (person?.roles || []) as PersonRole[],
          ResourceEnum.project,
          projectId
        );
        return personRole?.role === RoleType.candidate;
      }),
    [projectPeople, projectId]
  );

  const teamId = project.team_id || toNumber(_teamId);

  const { emitEvent } = useEvent();

  useEffect(() => {
    emitEvent({
      type: EVENT_TYPES.PERSON_VIEW,
      params: {
        person_id: candidateId,
      },
    });
  }, []);

  const { team, loading: loadingTeam } = useTeamDetails(teamId);

  const { assessments } = useAssessmentsList({
    projectId: projectId,
    personId: candidateId,
  });

  const personAssessment = first(assessments);

  const { people, loading: loadingPeople } = usePeople({
    team_id: project.team_id,
  });

  const {
    person,
    loading: loadingPerson,
    handleUpsertPerson,
  } = usePerson(candidateId);

  const surveyConfig = get(project, "config.dimensions", []);

  const { attachments: projectAttachments } = useAttachments({
    position_id: projectId,
  });

  const { dataList, attachments, addableDataItems, dimensionScoresData } =
    usePersonProfileData({
      person,
      filter: {
        position_id: projectId!,
        person_id: candidateId,
      },
      surveyConfig,
    });

  const {
    notes,
    loading: loadingNotes,
    handleupsertProjectNote,
    handleDeleteProjectNote,
    deleting: deletingNotes,
  } = useProjectNotes(projectId, candidateId);

  // If the account has summary fields configured, use them, otherwise use the default summary section
  const summarySections = (getAccountConfigValue(
    "feature_config.candidate_profile.summary_fields"
  ) as any[]) || [
    {
      title: "Summary",
      type: "candidate_summary",
      gpt_flow_id: getGptFlowKey("candidate_profile_summary"),
    },
  ];

  const summaryNote = find(notes, { type: "candidate_summary" });

  const {
    dimensionsData,
    loading: loadingDimensions,
    dimensions,
  } = useDimensionsWithScores(
    {
      team_id: toNumber(project.team_id),
      position_id: toNumber(projectId),
      people: [candidateId],
      type: surveyConfig,
      isLoading: loadingPeople || loadingProject || !project?.id,
    },
    [person, ...(people || [])]
  );

  // Filter leadership span dimensions
  const leadershipSpanDimensions = useMemo(() => {
    return (
      dimensions?.filter((dim) =>
        LEADERSHIP_SPAN_DIFFERENTIATORS.includes(dim.code as LSpanKey)
      ) || []
    );
  }, [dimensions]);

  const handleSaveCandidateSummary = useCallback(
    (type: string, id: number) => (summary: string) => {
      const existingNote = find(notes, {
        type: type || "candidate_summary",
      });
      handleupsertProjectNote({
        id: existingNote?.id,
        text: summary,
        type: type || "candidate_summary",
        projectId: projectId,
        personId: candidateId,
      });
    },
    [handleupsertProjectNote, projectId, notes]
  );

  const handleDeleteCandidateSummary = useCallback(
    (type: string, id: number) => () => {
      const existingNote = find(notes, {
        type: type || "candidate_summary",
      });
      if (existingNote) {
        handleDeleteProjectNote(id);
      }
    },
    [handleDeleteProjectNote, notes]
  );

  const handleEvaluateClick = useCallback(() => {
    navigate(
      `projects/${projectId}/person/${candidateId}/assessment/${
        personAssessment?.id || "new"
      }`
    );
  }, [candidateId, projectId, personAssessment]);

  const { scorecard } = useScorecard(toNumber(project?.scorecard_id));

  const { t } = useTranslation();

  const {
    personalityComplete,
    competenciesComplete,
    valuesComplete,
    allDimensionsComplete,
  } = useProfileCompletion(dimensionsData);

  const { layoutProps, tagsData, loadingMessage } = useProfileLayoutProps({
    contextType: "candidate_profile",
    entity: person,
    dimensionsData,
    surveyConfig,
    people: [person],
    attachments: [...(attachments || []), ...(projectAttachments || [])],
    loadingStates: [
      loadingDimensions,
      loadingPeople,
      loadingTeam,
      loadingPerson,
    ],
    additionalScope: {
      team_id: team.id!,
      assessment_id: personAssessment?.id!,
      project_id: projectId,
    },
  });

  // SummarySectionsGroup requires survey_config to be string[], so we construct a separate scope
  const summarySectionScope = {
    team_id: team.id!,
    person_id: person.id!,
    assessment_id: personAssessment?.id!,
    project_id: projectId,
    survey_config: surveyConfig,
  };

  const candidateName = person.first_name || "the candidate";

  return (
    <>
      <HeaderContent>
        <IconButton
          icon="ChevronLeft"
          size="md"
          label="Back to project"
          // variant="outline"
          // color="black""
          onClick={() => {
            navigate(`/projects/${projectId}`);
          }}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              variant="outline"
              icon="ToolsAssesment"
              color="black"
              label="Evaluate candidate"
              onClick={handleEvaluateClick}
              disabled={!project.scorecard_id}
            />
          </TooltipTrigger>
          {!project.scorecard_id && (
            <TooltipContent>
              Add assessment criteria to the project to evaluate candidates
            </TooltipContent>
          )}
        </Tooltip>
        <PDFExportTrigger
          documentTitle={`${person.name} - ${project.name}`}
          TriggerComponent={(props) => (
            <IconButton
              color="black"
              icon="Print"
              label="Print candidate"
              variant="outline"
              {...props}
            />
          )}
          PDFComponent={
            <CandidateProfilePdf
              summary={summaryNote?.text || ""}
              dimensions={dimensionsData}
              profile={person}
              scorecard={scorecard}
            />
          }
        />
        <PPTGeneratorButton
          templateName="rra-psych-only-report"
          data={{
            candidateName: person.name,
            projectTitle: project.name,
            companyName: project.client_name,
            likelyStrengths: cleanupMarkdownFromTags(
              find(notes, { type: "key_strengths_summary" })?.text || ""
            ),
            areasToProbe: cleanupMarkdownFromTags(
              find(notes, { type: "key_development_opportunities_summary" })
                ?.text || ""
            ),
            createdBy: viewer?.name || "",
            dimensionsData: dimensionsData,
            leadershipSpan: dimensionScoresData.LSpan,
          }}
          disabled={some(dimensionScoresData.LSpan, (d) =>
            isNil(d.score?.value)
          )}
          tooltip={t("projects.ppt_leadership_span_tooltip")}
          fileName={`${person.name}-${project.name}.pptx`}
        />
        <ShareCandidateTrigger
          personId={candidateId}
          projectId={projectId}
          orgId={project?.org_id!}
        />
      </HeaderContent>
      <Breadcrumbs
        items={[
          { label: project.name || "", path: `/projects/${projectId}` },
          {
            label: person.name || "" || person.email || "",
            options: map(otherCandidates, (_candidate) => ({
              label: _candidate?.name || "" || _candidate?.email || "",
              path: `/projects/${projectId}/person/${_candidate?.id}`,
            })),
          },
        ]}
      />
      <ProfileLayout {...layoutProps} loadingMessage={loadingMessage}>
        <ProfileHeader
          initials={person.initials || ""}
          name={person.name || person.email || ""}
          avatarUrl={person.avatar || ""}
          teamName={team?.name || ""}
          positionName={project.name || ""}
          email={person.email || ""}
          phone={person.phone || ""}
          linkedin={person.linkedin || ""}
          canEdit={hasPermissionsToEdit}
          onContactChange={handleUpsertPerson}
          isContact={!!person.is_contact}
        />
        <ProfileEmptyState
          variant="person"
          context="candidate"
          person={person}
          projectId={projectId}
          surveyConfig={surveyConfig}
          visible={
            !allDimensionsComplete &&
            import.meta.env.VITE_GPT_FLOW_CONFIG !== "rraqa"
          }
        />
        <FileUploadSection
          person_id={candidateId}
          position_id={projectId}
          title="Add documents to enrich the candidate profile"
          subtitle="Upload interview notes, CVs, or other relevant documents."
          description="AssessioAI combines insights from uploaded documents and psychometric data to deliver a deeper, evidence-based view of each candidate."
        />
        <DataSection
          title="Candidate data"
          description="Manage data the candidate is associated with"
          items={dataList as any}
          canEdit={hasPermissionsToEdit}
          addableItems={addableDataItems}
        />
        <ProfileSectionWrapper label="summary" id="summary">
          <SummarySectionsGroup
            sections={summarySections}
            notes={notes}
            onDeleteSummary={handleDeleteCandidateSummary}
            onSaveSummary={handleSaveCandidateSummary}
            scope={summarySectionScope}
            loading={loadingNotes || deletingNotes}
            useSingleGenerator={summarySections.length > 1}
            tagsData={tagsData}
          />
        </ProfileSectionWrapper>
        <ProfileSectionWrapper label="notes" id="notes">
          <NotesSection
            loading={loadingNotes || deletingNotes}
            notes={filter(notes, { type: "note" })}
            onCreateNote={handleupsertProjectNote}
            onDeleteNote={handleDeleteProjectNote}
            onEditNote={handleupsertProjectNote}
            readOnly={!hasPermissionsToEdit}
          />
        </ProfileSectionWrapper>
        {!loadingTeam && !loadingDimensions && (
          <>
            {leadershipSpanDimensions.length > 0 && (
              <FeatureGate flagKey={FeatureFlagKey.LEADERSHIP_SPAN}>
                <ProfileSectionWrapper
                  label="leadershipSpan"
                  id="leadershipSpan"
                >
                  <LeadershipSpanWidget
                    leadershipSpan={leadershipSpanDimensions}
                  />
                </ProfileSectionWrapper>
              </FeatureGate>
            )}
            {allDimensionsComplete && (
              <ProfileSectionWrapper label="highlights" id="highlights">
                <WisnioProfileHighlightsSection dimensions={dimensionsData} />
              </ProfileSectionWrapper>
            )}
            {personalityComplete && (
              <ProfileSectionWrapper label="personality" id="personality">
                <PersonalityMapSection
                  dimensions={dimensionsData.personality}
                  context="candidate"
                />
              </ProfileSectionWrapper>
            )}
            {valuesComplete && (
              <ProfileSectionWrapper label="values" id="values">
                <ProfileValuesSection
                  dimensions={dimensionsData.values}
                  context="candidate"
                  defaultPersonId={candidateId}
                />
              </ProfileSectionWrapper>
            )}
            {competenciesComplete && (
              <ProfileSectionWrapper label="competencies" id="competencies">
                <CompetencyMapSection
                  context="candidate"
                  dimensions={dimensionsData.competencies}
                  defaultPersonId={candidateId}
                  // projectCompetencies={scorecard.competencies}
                  title={t("profile.competency_scores")}
                />
                <CandidateCompetencyInsightsSection
                  person={person}
                  dimensions={dimensionsData?.competencies}
                  scorecardRows={scorecard.rows as ScorecardRow[]}
                />
              </ProfileSectionWrapper>
            )}
          </>
        )}
      </ProfileLayout>
    </>
  );
}

export default CandidateProfile;
